import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { toInlineTextContent } from 'components/richText';
import { TextMarkEditFrame, TextMarkGap, TextMarkInput } from './placeholderNodes.styles';
import type { PlaceholderExtensionOptions } from './placeholderNodes';

// Edit mode opens the slot on a plain caret landing but not on a drag or Shift-range sweep crossing
// the chip, which would steal focus and collapse the selection here. Track pointer/Shift to tell them apart.
let pointerIsDown = false;
let shiftIsDown = false;
if (typeof document !== 'undefined') {
  document.addEventListener('mousedown', () => (pointerIsDown = true), true);
  document.addEventListener('mouseup', () => (pointerIsDown = false), true);
  document.addEventListener('keydown', (event) => (shiftIsDown = event.shiftKey), true);
  document.addEventListener('keyup', (event) => (shiftIsDown = event.shiftKey), true);
  // A release fired in another window never reaches us, so clear the flags when focus leaves.
  window.addEventListener('blur', () => {
    pointerIsDown = false;
    shiftIsDown = false;
  });
}

export const TextMarkPlaceholder = ({
  node,
  updateAttributes,
  selected,
  getPos,
  extension,
  editor,
}: ReactNodeViewProps) => {
  const name = node.attrs.name as string;
  const value = node.attrs.value as string;
  const inputRef = useRef<HTMLInputElement>(null);
  const { onAdvance, onRetreat, consumeActivation } =
    extension.options as PlaceholderExtensionOptions;

  // Latched: typing fires updateAttributes, whose transaction drops the node-selection, so edit mode
  // ends only when focus leaves the input.
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!selected) return undefined;
    const pos = getPos();
    if (pos === undefined) return undefined;

    if (consumeActivation(pos)) {
      setEditing(true);
      return undefined;
    }

    const openIfStillSelected = () => {
      const sel = editor.state.selection;
      if (sel instanceof NodeSelection && sel.from === pos) setEditing(true);
    };

    // A click node-selects the chip with the button down; open on release if it's still selected.
    if (pointerIsDown) {
      document.addEventListener('mouseup', openIfStillSelected, { once: true });
      return () => document.removeEventListener('mouseup', openIfStillSelected);
    }

    // A Shift range extension flashes a transient node-selection as it crosses the atom. Ignore it,
    // and for a plain caret landing wait for the selection to settle before opening.
    if (shiftIsDown) return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(openIfStillSelected);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [selected, editor, getPos, consumeActivation]);

  const handlerState = useRef({ getPos, node, editor, onAdvance, onRetreat });
  useEffect(() => {
    handlerState.current = { getPos, node, editor, onAdvance, onRetreat };
  }, [getPos, node, editor, onAdvance, onRetreat]);

  // Native keydown so it runs before ProseMirror's own (e.g. Tab must not hop between inputs).
  useEffect(() => {
    if (!editing) return undefined;
    const input = inputRef.current;
    if (!input) return undefined;

    const raf = requestAnimationFrame(() => input.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      const { getPos, node, editor, onAdvance, onRetreat } = handlerState.current;
      const pos = getPos();
      if (pos === undefined) return;
      const after = pos + node.nodeSize;

      const commitValue = () => {
        // toInlineTextContent, not the raw string: insertContentAt parses a bare string as HTML,
        // which would drop text like "<38".
        editor
          .chain()
          .focus()
          .insertContentAt({ from: pos, to: after }, toInlineTextContent(input.value))
          .run();
      };

      const submitAndAdvance = () => {
        if (input.value.length > 0) {
          commitValue();
          onAdvance(pos);
        } else if (!onAdvance(after)) {
          editor.chain().focus().setTextSelection(after).run();
        }
      };

      const submitAndRetreat = () => {
        if (input.value.length > 0) commitValue();
        if (!onRetreat(pos)) {
          editor.chain().focus().setTextSelection(pos).run();
        }
      };

      if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault();
        event.stopPropagation();
        submitAndAdvance();
        return;
      }

      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        submitAndRetreat();
        return;
      }

      if (event.key === 'Backspace' && input.value.length === 0) {
        event.preventDefault();
        event.stopPropagation();
        editor.chain().focus().deleteRange({ from: pos, to: after }).run();
        return;
      }

      const plainArrow = !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
      const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
      const atEnd =
        input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
      if (plainArrow && (event.key === 'ArrowUp' || (event.key === 'ArrowLeft' && atStart))) {
        event.preventDefault();
        event.stopPropagation();
        editor.chain().focus().setTextSelection(pos).run();
      } else if (
        plainArrow &&
        (event.key === 'ArrowDown' || (event.key === 'ArrowRight' && atEnd))
      ) {
        event.preventDefault();
        event.stopPropagation();
        editor.chain().focus().setTextSelection(after).run();
      }
    };

    input.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      input.removeEventListener('keydown', handleKeyDown);
    };
  }, [editing]);

  // A window blur can drop focus off the input; the OS may not restore it, so re-focus on return.
  useEffect(() => {
    if (!editing) return undefined;
    const onWindowFocus = () => {
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement !== inputRef.current)
          inputRef.current.focus();
      });
    };
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, [editing]);

  const activate = () => {
    const pos = getPos();
    if (pos !== undefined) editor.chain().focus().setNodeSelection(pos).run();
  };

  const empty = value.length === 0;
  return (
    <NodeViewWrapper as="span" data-ph-no-autocapture>
      <TextMarkEditFrame
        // `selected` drops on every keystroke's transaction; `editing` stays latched through the edit.
        $active={editing || selected}
        {...(editing
          ? {}
          : {
              role: 'button',
              'aria-label': name,
              title: name,
              tabIndex: -1,
              // onMouseDown + preventDefault keeps the editor selection intact through the click.
              onMouseDown: (event: ReactMouseEvent) => {
                event.preventDefault();
                activate();
              },
            })}
      >
        {empty ? <span aria-hidden="true">[</span> : null}
        {editing ? (
          <TextMarkInput
            ref={inputRef}
            // Uncontrolled so mid-text edits keep the caret; the value is read back on commit and blur.
            defaultValue={value}
            aria-label={name}
            tabIndex={-1}
            onBlur={(event) => {
              if (event.currentTarget.value !== value) {
                updateAttributes({ value: event.currentTarget.value });
              }
              // A window blur (no relatedTarget, document unfocused) keeps the slot open for the return.
              if (!event.relatedTarget && !document.hasFocus()) return;
              setEditing(false);
            }}
          />
        ) : empty ? (
          <TextMarkGap aria-hidden="true" />
        ) : (
          value
        )}
        {empty ? <span aria-hidden="true">]</span> : null}
      </TextMarkEditFrame>
    </NodeViewWrapper>
  );
};
